import {
  Component,
  OnInit,
  OnChanges,
  SimpleChanges,
  Input,
  Output,
  EventEmitter
} from '@angular/core';
import {
  FormGroup,
  FormControl,
  Validators,
  FormBuilder
} from '@angular/forms';
import { ContainerStatus, ContainerType } from 'src/app/enum/enum';
import { ContainersService } from '../../Service/containers.service';
import { CustomToastsService } from 'src/app/Services/custom-toasts.service';
import { ErrorHandlerService } from 'src/app/Services/error-handler.service';
import { MasterDataSeriveService } from 'src/app/Services/master-data-serive.service';
import {
  containerRequestModel,
  containerResponse
} from '../../Model/Container';
import { Stores } from 'src/app/Models/ApplicationModels';

@Component({
  selector: 'app-add-update-container',
  templateUrl: './add-update-container.component.html',
  styleUrls: ['./add-update-container.component.css']
})
export class AddUpdateContainerComponent implements OnInit, OnChanges {
  @Input() container: containerResponse = new containerResponse();
  @Input() CompanyId: number = 0;
  @Input() StoreId: number = 0;
  @Output() saved = new EventEmitter<void>();
  @Output() resetFormEvent = new EventEmitter<void>();

  containerForm!: FormGroup;
  containerRequestModel: containerRequestModel = new containerRequestModel();
  BinStatusEnum = ContainerStatus;
  stores: Stores[] = [];

  BinStatusList = Object.entries(ContainerStatus)
    .filter(
      ([_, value]) =>
        typeof value === 'number' && value !== ContainerStatus.Unknown
    )
    .map(([key, value]) => ({
      label: key,
      value: value as number
    }));

  BinTypeList = Object.entries(ContainerType)
    .filter(
      ([_, value]) =>
        typeof value === 'number' && value !== ContainerType.Unknown
    )
    .map(([key, value]) => ({
      label: key,
      value: value as number
    }));

  constructor(
    private fb: FormBuilder,
    private containersService: ContainersService,
    private toastr: CustomToastsService,
    private errorService: ErrorHandlerService,
    private masterService: MasterDataSeriveService
  ) {}

  ngOnInit() {
    this.initializeForm();
    if (this.CompanyId > 0) {
      this.getAllStores();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['container'] && this.containerForm) {
      this.containerForm.patchValue({
        StoreId: this.container.storeId || null,
        Referenceno: this.container.referenceNumber || '',
        ExternalId: this.container.externalId || null
      });
      if (this.container.id > 0 || this.container.referenceNumber) {
        this.containerForm.get('Referenceno')?.disable();
      } else {
        this.containerForm.get('Referenceno')?.enable();
      }
    }
  }

  getAllStores() {
    this.masterService.getallstores(this.CompanyId).subscribe({
      next: (res: any) => {
        this.stores = res;
      },
      error: (err: any) => {
        this.errorService.getErrorMessage(err);
      }
    });
  }

  initializeForm() {
    this.containerForm = new FormGroup({
      Referenceno: new FormControl(
        {
          value: this.container.referenceNumber || '',
          disabled: this.container.id > 0
        },
        this.container.id > 0 ? Validators.required : null // no validator on create
      ),
      description: new FormControl('', Validators.required),
      BinStatus: new FormControl(''),
      isLabelPrint: new FormControl(false),
      ExternalId: new FormControl('', Validators.required),
      StoreId: new FormControl(
        this.container.storeId || null,
        Validators.required
      )
    });
  }

  resetForm(): void {
    this.container = new containerResponse();
    this.container.id = 0;
    this.initializeForm();
    this.resetFormEvent.emit();
  }

  addEditContainer(container: containerResponse) {
    if (!this.containerForm) {
      this.initializeForm();
    }
    this.containerRequestModel.companyId = this.CompanyId;
    this.containerRequestModel.storeId =
      this.containerForm.get('StoreId')?.value || this.StoreId;
    this.containerRequestModel.referenceNumber =
      this.containerForm.get('Referenceno')?.value;
    this.containerRequestModel.description = this.container.description;
    this.containerRequestModel.externalId =
      this.containerForm.get('ExternalId')?.value;

    if (this.container.id == 0) {
      this.CreateBin(this.container);
    } else {
      this.UpdateBin(this.container);
    }
  }

  getContainerById(id: number) {
    this.containersService.getContainerById(id).subscribe({
      next: (response: any) => {
        this.container = response;
        this.containerRequestModel.referenceNumber =
          this.container.referenceNumber;
        this.containerRequestModel.description = this.container.description;
        this.containerRequestModel.type = this.container.type;
        this.containerRequestModel.status = this.container.status;
        this.containerForm.patchValue({
          ExternalId: this.container.externalId || null,
          StoreId: this.container.storeId || this.StoreId
        });
        this.containerForm.get('Referenceno')?.disable();
      },
      error: (error: any) => {
        this.errorService.getErrorMessage(error);
      }
    });
  }

  private async CreateBin(container: containerResponse) {
    try {
      this.containerRequestModel.type = ContainerType.Production;
      this.containerRequestModel.status = ContainerStatus.Available;
      this.containersService
        .createContainer(this.containerRequestModel)
        .subscribe({
          next: (response: any) => {
            this.toastr.success('Bin created successfully');
            this.saved.emit();
            this.resetForm();
          },
          error: (error: any) => {
            const errorBody = error.error;
            if (error.status === 400 && errorBody?.errors?.length) {
              const err = errorBody.errors[0];
              this.toastr.error(`${err.message}`);
            } else {
              this.toastr.error(errorBody?.detail || 'Failed to create Bin.');
            }
          }
        });
    } catch (error) {}
  }

  private async UpdateBin(container: containerResponse) {
    try {
      this.containerRequestModel.type = container.type;
      this.containerRequestModel.status = container.status;
      this.containerRequestModel.id = container.id;
      this.containersService
        .updateContainer(container.id, this.containerRequestModel)
        .subscribe({
          next: (response: any) => {
            this.toastr.success('Container updated successfully');
            this.saved.emit();
            this.resetForm();
          },
          error: (error: any) => {
            const errorBody = error.error;
            if (error.status === 400 && errorBody?.errors?.length) {
              const err = errorBody.errors[0];
              this.toastr.error(`${err.message}`);
            } else {
              this.toastr.error(errorBody?.detail || 'Failed to create Bin.');
            }
          }
        });
    } catch (error) {}
  }

  onPrintClick() {
    this.container.isLablePrint = !this.container.isLablePrint;
  }
}
